import { useEffect, useState } from "react";

import auraResidenceImg from "../../assets/images/projects/aura-residence-marketing.webp";
import communiGuardImg from "../../assets/images/projects/CommuniGuard.webp";
import svccRotc from "../../assets/images/projects/rotc.webp";
import omniDiningImg from "../../assets/images/projects/smartOrderingSystem.webp";
import campusFlowImg from "../../assets/images/projects/proflocate.webp";

import { motion, AnimatePresence } from "framer-motion";
import { DraggableCarousel } from "../project/scroll-x-carousel";
import {
  CardHoverReveal,
  CardHoverRevealContent,
  CardHoverRevealMain,
} from "../project/reveal-on-hover";
import { Badge } from "../project/badge";

// TODO: Replace dummy images with local project screenshots. Recommended resolution: 1920x1080 (16:9 ratio).
const SLIDES = [
  {
    id: "slide-1",
    imageUrl: auraResidenceImg,
    title: "Aura Residences Management Platform",
    description:
      "A comprehensive, full-stack ecosystem for property management, featuring distinct Admin and Tenant portals. Built to streamline operations, it handles real-time property inventory status, complex monthly billing generation with automated deduction tracking, and a robust appointment scheduling system. The platform integrates SMTP/Gmail APIs to manage automated dual-party email notifications for inquiries and confirmations.",
    previewUrl: "#",
    type: "Full-Stack",
    techStack: [
      "React",
      "Node.js",
      "Express",
      "Supabase",
      "Framer Motion",
      "Tailwind CSS",
      "Gmail API",
      "SMTP",
    ],
  },
  {
    id: "slide-2",
    title: "CommuniGuard Residential System",
    description:
      "A dual-platform web and mobile ecosystem for community management. Features interactive Google Maps for exact resident geolocation, real-time private and community-wide chat via Supabase, and a robust emergency alert engine that dispatches urgency-based, expiring push notifications to mobile users.",
    techStack: [
      "React Native",
      "Express",
      "PostgreSQL",
      "Supabase",
      "Tailwind CSS",
    ],
    type: "Web & Mobile App",
    previewUrl: "#",
    imageUrl: communiGuardImg,
  },
  {
    id: "slide-3",
    title: "ROTC & Management System",
    description:
      "An enterprise-scale operational management platform engineered with a 4-tier JWT role architecture (Personnel, Special Personnel, Admin, and Super-Admin). Built to optimize high-volume organizational tasks, it features dynamic QR-code automation for instant attendance processing, transactional asset and inventory lifecycle tracking, and a modular point-based merit and conduct evaluation engine.",
    techStack: [
      "React",
      "Node.js",
      "Express",
      "MongoDB",
      "Material UI",
      "React Query",
      "JWT",
    ],
    type: "Full-Stack Web App",
    previewUrl: "#",
    imageUrl: svccRotc,
  },
  {
    id: "slide-4",
    title: "CampusFlow Faculty Routing System",
    description:
      "A real-time directory and scheduling platform designed to bridge communication gaps in large institutions. Features a public-facing portal for live location tracking and availability status, paired with a robust administrative dashboard for logistical CRUD operations and automated, dynamically generated PDF schedule reports.",
    techStack: [
      "React",
      "Node.js",
      "Express",
      "Tailwind CSS",
      "PDF Generation",
    ],
    type: "Full-Stack Web App",
    previewUrl: "#",
    imageUrl: campusFlowImg,
  },
  {
    id: "slide-5",
    title: "OmniDine POS & Smart Ordering Ecosystem",
    description:
      "A comprehensive restaurant management platform featuring a dual-interface architecture. It combines a staff Point-of-Sale (POS) with real-time, ingredient-level inventory deduction, and a touchless customer portal driven by dynamically generated, table-specific QR codes. Includes virtual staff notifications, role-based shift tracking, and a robust administrative analytics dashboard.",
    techStack: ["React", "Node.js", "Express", "MongoDB", "Tailwind CSS"],
    type: "Full-Stack Web App",
    previewUrl: "#",
    imageUrl: omniDiningImg,
  },
];

export default function Project() {
  const [activeIndex, setActiveIndex] = useState(0);
  const duplicatedSlides = [...SLIDES, ...SLIDES];

  useEffect(() => {
    // This observer creates a narrow vertical band in the exact center of the screen
    // It detects which card is currently passing through the center.
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.getAttribute("data-index"));
            setActiveIndex(index);
          }
        });
      },
      {
        root: null,
        rootMargin: "0px -45% 0px -45%", // Triggers when a card hits the middle 10% of the screen
        threshold: 0,
      },
    );

    const cards = document.querySelectorAll(".project-card");
    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="projects"
      className="py-20 overflow-hidden relative h-[100vh] flex flex-col justify-between"
    >
      {/* Header Section */}
      <div className="container mx-auto px-6 relative z-20 flex flex-col items-center text-center mt-4 shrink-0">
        <h2 className="text-4xl md:text-5xl font-bold text-[#34abe1] tracking-tight">
          Projects
        </h2>
        <p className="mt-4 text-base md:text-lg max-w-2xl text-white opacity-80">
          A showcase of my work, highlighting my tech stack and problem-solving
          approach.
        </p>
      </div>

      {/* Edge Gradients */}
      <div className="pointer-events-none w-[10vw] h-full absolute top-0 left-0 z-10 bg-gradient-to-r from-base-100 to-transparent" />
      <div className="pointer-events-none w-[10vw] h-full absolute top-0 right-0 z-10 bg-gradient-to-l from-base-100 to-transparent" />

      {/* Carousel Area */}
      <div className="flex-grow flex items-center relative z-20">
        <DraggableCarousel className="pl-8 pb-4">
          {duplicatedSlides.map((slide, index) => (
            <CardHoverReveal
              key={`${slide.id ?? index}-${index}`}
              data-index={index % SLIDES.length} // We use modulo so duplicated slides map back to the original 0-3 index
              className="project-card w-[85vw] sm:w-[60vw] md:w-[45vw] lg:w-[35vw] xl:w-[28vw] shadow-xl border border-white/10 rounded-xl shrink-0"
            >
              <CardHoverRevealMain>
                <img
                  alt={slide.title}
                  src={slide.imageUrl}
                  className="size-full aspect-video object-cover"
                />
              </CardHoverRevealMain>

              {/* Hover content is now STRICTLY for technical specs to avoid redundant text */}
              <CardHoverRevealContent className="flex flex-col gap-3 rounded-xl bg-black/85 backdrop-blur-xl p-4 sm:p-5 border border-white/10 max-h-[90%] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                <div className="space-y-1.5">
                  <h3 className="text-[10px] sm:text-xs font-semibold text-white/60 uppercase tracking-wider">
                    Type
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    <Badge
                      variant="default"
                      className="capitalize text-[10px] px-2 py-0"
                    >
                      {slide.type}
                    </Badge>
                  </div>
                </div>

                <div className="space-y-1.5 pt-2">
                  <h3 className="text-[10px] sm:text-xs font-semibold text-white/60 uppercase tracking-wider">
                    Tech Stack
                  </h3>
                  <div className="flex flex-wrap gap-1.5">
                    {slide.techStack.map((tech) => (
                      <Badge
                        key={tech}
                        variant="secondary"
                        className="capitalize text-[10px] px-2 py-0"
                      >
                        {tech}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardHoverRevealContent>
            </CardHoverReveal>
          ))}
        </DraggableCarousel>
      </div>

      {/* Fixed Bottom Title & Description Panel */}
      <div className="relative z-30 h-48 sm:h-40 w-full shrink-0 flex items-start justify-center text-center px-6 pointer-events-none">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeIndex} // This key forces the animation to re-run whenever the activeIndex changes
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -20, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="max-w-2xl mx-auto flex flex-col items-center pointer-events-auto"
          >
            <h3
              className="text-2xl md:text-3xl font-bold 
  bg-gradient-to-r from-primary via-blue-500 to-purple-500 
  bg-clip-text text-transparent mb-3 tracking-tight"
            >
              {SLIDES[activeIndex]?.title}
            </h3>

            <p className="text-sm md:text-base text-white/70 leading-relaxed mb-4">
              {SLIDES[activeIndex]?.description
                ?.split(" ")
                .map((word, index) => (
                  <motion.span
                    key={index}
                    initial={{ filter: "blur(10px)", opacity: 0, y: 5 }}
                    animate={{ filter: "blur(0px)", opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.2,
                      ease: "easeInOut",
                      delay: 0.015 * index, // Staggered word animation
                    }}
                    className="inline-block"
                  >
                    {word}&nbsp;
                  </motion.span>
                ))}
            </p>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
